import { useEffect, useState } from "react";
import { Trophy, EyeOff, Users, BadgeCheck, Globe, Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageTitle } from "@/components/Layout";
import { ShareCode, CircleWinner } from "@/components/CircleShare";

const Row = ({ r, testid }) => {
  const { t } = useApp();
  const medal = ["bg-[#FFD166]", "bg-[#D2E0D8]", "bg-[#F4A97F]"][r.rank - 1];
  return (
    <div data-testid={testid}
      className={`flex items-center gap-3 rounded-2xl border p-3 ${r.is_me ? "border-[#E05C3A] bg-[#E05C3A]/10" : "border-[#D2E0D8] bg-white"}`}>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display font-extrabold text-[#0D3B2B] ${medal || "bg-[#F4F8F5]"}`}>{r.rank}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-[#0D3B2B]">{r.name}{r.is_me && <span className="ml-2 text-xs font-bold text-[#E05C3A]">{t("You")}</span>}</p>
        <p className="flex items-center gap-1 truncate text-xs text-[#7B968B]">{r.pickups} {t(r.pickups === 1 ? "pickup" : "pickups")} · <Leaf className="h-3 w-3 text-[#2E9E6B]" />{r.co2} kg CO₂ {t("saved")}</p>
      </div>
      <p className="font-display text-lg font-bold text-[#0D3B2B]">{r.kg} kg</p>
    </div>
  );
};

const SCOPES = [["circle", "My circle", Users], ["type", "Like me", BadgeCheck], ["all", "All Jaipur", Globe]];

export default function Leaderboard() {
  const { t } = useApp();
  const [period, setPeriod] = useState("month");
  const [scope, setScope] = useState("circle");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/leaderboard", { params: { period, scope } }).then((r) => setData(r.data)).catch(() => {});
  }, [period, scope]);

  const meInTop = data?.rows.some((r) => r.is_me);
  const fellBack = data && data.asked === "circle" && data.scope === "all";
  return (
    <div className="space-y-4">
      <PageTitle sub={t("Ranked by kg recycled. Names are masked to protect privacy.")}>{t("Leaderboard")}</PageTitle>
      <div className="grid grid-cols-3 gap-2" data-testid="lb-scope">
        {SCOPES.map(([v, l, Icon]) => (
          <button key={v} onClick={() => setScope(v)} data-testid={`lb-scope-${v}`}
            className={`flex h-14 flex-col items-center justify-center rounded-2xl border-2 text-xs font-bold transition-colors ${scope === v ? "border-[#2E9E6B] bg-[#2E9E6B]/10 text-[#0D3B2B]" : "border-[#D2E0D8] bg-white text-[#4A6358]"}`}>
            <Icon className="h-5 w-5" />{v === "type" && data?.my_type ? t(data.my_type) : t(l)}</button>
        ))}
      </div>
      <div className="flex rounded-2xl bg-[#EAEFEA] p-1">
        {[["month", "This month"], ["all", "All time"]].map(([v, l]) => (
          <button key={v} onClick={() => setPeriod(v)} data-testid={`lb-period-${v}`}
            className={`h-11 flex-1 rounded-xl text-sm font-bold transition-colors ${period === v ? "bg-white text-[#0D3B2B] shadow-sm" : "text-[#4A6358]"}`}>{t(l)}</button>
        ))}
      </div>
      {scope === "circle" && (
        <div className="space-y-2">
          <CircleWinner w={data?.circle_winner} code={data?.ref_code} />
          <p className="rounded-2xl bg-[#F4F8F5] p-3 text-sm text-[#4A6358]" data-testid="lb-circle-note">
            {fellBack ? t("Your circle is empty, so here are the top recyclers in Jaipur. Invite friends with your referral code to build your circle.")
              : t("Your circle: friends who joined with your referral code, and the friend whose code you used.")}
            {" "}{t("Each month, the top recycler in your circle gets bonus points.")}</p>
          {(fellBack || data?.circle_size < 3) && <ShareCode code={data?.ref_code} />}
        </div>
      )}
      {data?.me_hidden && (
        <p className="flex items-center gap-2 rounded-2xl bg-[#EAEFEA] p-3 text-sm text-[#4A6358]" data-testid="lb-hidden-note">
          <EyeOff className="h-4 w-4" /> {t("You are hidden from others. Only you can see your rank.")} <Link to="/settings" className="font-semibold text-[#2E9E6B]">{t("Settings")}</Link>
        </p>
      )}
      {data && data.me && !meInTop && (
        <div className="space-y-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[#E05C3A]">{t("Your rank")}</p>
          <Row r={data.me} testid="lb-my-row" />
        </div>
      )}
      <div className="space-y-2" data-testid="lb-list">
        {data?.rows.map((r) => <Row key={r.rank} r={r} testid={r.is_me ? "lb-my-row" : `lb-row-${r.rank}`} />)}
        {data && !data.rows.length && (
          <div className="py-10 text-center text-[#4A6358]"><Trophy className="mx-auto mb-2 h-10 w-10 text-[#7B968B]" />{t("No one here yet. Be the first!")}</div>
        )}
      </div>
      {data && <p className="text-center text-xs text-[#7B968B]" data-testid="lb-total">{t("Showing top")} {Math.min(100, data.total)} {t("of")} {data.total}</p>}
    </div>
  );
}
